"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";

import IconCheck from "@/components/Icons/IconCheck";
import Modal from "@/components/ui/Modal";

interface BankValuationFeaturesProps {
  priceLabel: string;
  priceLine: string;
  printFeature: string;
  acceptedFeature: string;
  variant: "desktop" | "mobile";
}

const BankValuationFeatures = ({
  priceLabel,
  priceLine,
  printFeature,
  acceptedFeature,
  variant,
}: BankValuationFeaturesProps) => {
  const t = useTranslations("BankValuation.pricingModal");
  const [open, setOpen] = useState(false);

  const wrapperClass =
    variant === "desktop"
      ? "mb-8 hidden flex-col items-center space-y-3 md:flex md:flex-row md:space-x-4 md:space-y-0 rtl:md:space-x-reverse"
      : "mb-8 flex-col items-center space-y-3 md:hidden";

  return (
    <>
      <div className={wrapperClass}>
        {/* Price card — same as siblings, only the price line is red + clickable */}
        <div className="flex items-center rounded-20 bg-white p-5 font-urbanist font-medium text-primary-foreground under lg:w-80">
          <IconCheck height="45" width="45" fill="white" bg="hsla(var(--primary))" />
          <span className="ms-2 text-sm lg:text-base">
            <span className="block">{priceLabel}</span>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="block cursor-pointer text-red-500  underline underline-offset-2"
            >
              {priceLine}
            </button>
          </span>
        </div>

        {/* Print — unchanged */}
        <div className="flex items-center rounded-20 bg-white p-5 font-urbanist font-medium text-primary-foreground">
          <IconCheck height="45" width="45" fill="white" bg="hsla(var(--primary))" />
          <span className="ms-2 text-sm lg:text-base">{printFeature}</span>
        </div>

        {/* Accepted — unchanged */}
        <div className="flex items-center rounded-20 bg-white p-5 font-urbanist font-medium text-primary-foreground">
          <IconCheck height="45" width="45" fill="white" bg="hsla(var(--primary))" />
          <span className="ms-2 text-sm lg:text-base">{acceptedFeature}</span>
        </div>
      </div>

      <Modal isOpen={open} onClose={() => setOpen(false)} size="small" className="!z-50">
        <div className="mb-6 pt-2">
          <h2 className="font-urbanist text-xl font-bold text-primary-foreground">{t("title")}</h2>
          <p className="inter mt-1 text-sm text-secondary-foreground">{t("subtitle")}</p>
        </div>

        <div className="space-y-3">
          {/* Alba Inspection */}
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-urbanist text-sm font-semibold text-primary-foreground">{t("albaTitle")}</p>
                <p className="inter mt-0.5 text-xs text-secondary-foreground">{t("albaDesc")}</p>
              </div>
              <div className="shrink-0 text-end">
                <p className="font-urbanist text-lg font-bold text-primary">{t("albaPrice")}</p>
                <p className="inter text-xs text-secondary-foreground">{t("vat")}</p>
              </div>
            </div>
          </div>

          {/* Home Inspection */}
          <div className="rounded-2xl  p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-urbanist text-sm font-semibold text-primary-foreground">{t("homeTitle")}</p>
                <p className="inter mt-0.5 text-xs text-secondary-foreground">{t("homeDesc")}</p>
              </div>
              <div className="shrink-0 text-end">
                <p className="font-urbanist text-lg font-bold text-primary">{t("homePrice")}</p>
                <p className="inter text-xs text-secondary-foreground">{t("vat")}</p>
              </div>
            </div>
          </div>

          {/* Certificate */}
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-urbanist text-sm font-semibold text-primary-foreground">{t("certificateTitle")}</p>
                <p className="inter mt-0.5 text-xs text-secondary-foreground">{t("certificateDesc")}</p>
              </div>
              <div className="shrink-0 text-end">
                <p className="font-urbanist text-lg font-bold text-primary">{t("certificatePrice")}</p>
                <p className="inter text-xs text-secondary-foreground">{t("vat")}</p>
              </div>
            </div>
          </div>
        </div>

        <p className="inter mt-4 text-center text-xs text-secondary-foreground">{t("note")}</p>
      </Modal>
    </>
  );
};

export default BankValuationFeatures;
